<template>
  <div class="panel">
    <div class="panel-title">Gestion des clés API</div>
    <div class="panel-subtitle">
      Générer des clés API par société et utilisateur (lecture seule ou
      lecture/écriture).
    </div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[32rem] w-full"
        :label="'Société'"
        :options="companyOptions"
        :value="selectedSchemaId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onCompanyChange"
      />
    </div>

    <template v-if="selectedSchemaId">
      <div v-if="dataLoading" class="mt-4 status-pill">
        Chargement des clés API…
      </div>

      <template v-else>
        <div class="mt-5 section-title">Clés API existantes</div>
        <div v-if="!apiKeys.length" class="empty-keys">
          Aucune clé API pour cette société.
        </div>
        <div v-else class="keys-table-wrap">
          <table class="keys-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Accès</th>
                <th>Facturable</th>
                <th>Prix / mois</th>
                <th>Active</th>
                <th>Créée le</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in apiKeys" :key="row.id">
                <td>{{ formatUserLabel(row) }}</td>
                <td>
                  <span
                    class="access-pill"
                    :class="
                      row.access_level === 'rw' ? 'access-rw' : 'access-ro'
                    "
                  >
                    {{ row.access_level === 'rw' ? 'RW' : 'RO' }}
                  </span>
                </td>
                <td>
                  <api-key-toggle-switch
                    :on="row.billable"
                    :disabled="patching"
                    :title="
                      row.billable
                        ? 'Facturable — cliquer pour désactiver'
                        : 'Non facturable — cliquer pour activer'
                    "
                    @toggle="onBillableSwitchClick(row)"
                  />
                </td>
                <td class="price-cell">
                  <button
                    v-if="row.billable"
                    type="button"
                    class="price-cell__btn"
                    :disabled="patching"
                    :title="'Modifier le prix'"
                    @click="openBillingEdit(row)"
                  >
                    {{ formatPrice(row.price_per_month) }}
                  </button>
                  <span v-else class="price-cell__na">—</span>
                </td>
                <td>
                  <api-key-toggle-switch
                    :on="row.active"
                    :disabled="patching"
                    :title="
                      row.active
                        ? 'Clé active — cliquer pour désactiver'
                        : 'Clé désactivée — cliquer pour réactiver'
                    "
                    @toggle="onActiveSwitchClick(row)"
                  />
                </td>
                <td>{{ formatDate(row.creation_date) }}</td>
                <td class="actions-cell">
                  <hit-button
                    color="button"
                    prefix-icon="key"
                    icon-size="sm"
                    :tooltip="'Voir la clé'"
                    :hide-border="true"
                    :remove-outline="true"
                    :padding-value="1"
                    :disabled="patching || tokenLoading"
                    @click="openTokenView(row)"
                  />
                  <hit-button
                    color="warning"
                    prefix-icon="trash"
                    icon-size="sm"
                    :tooltip="'Révoquer'"
                    :hide-border="true"
                    :remove-outline="true"
                    :padding-value="1"
                    :disabled="patching || tokenLoading"
                    @click="openRevokeConfirm(row)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-6 section-title">Nouvelle clé API</div>
        <div class="generate-card">
          <div v-if="!userOptions.length" class="generate-card__alert">
            Tous les utilisateurs de cette société ont déjà une clé API.
          </div>

          <template v-else>
            <p class="generate-card__hint">
              Créez une clé pour un utilisateur de la société qui n’en possède
              pas encore.
            </p>

            <div class="generate-form">
              <div class="generate-form__block">
                <div class="generate-form__block-title">Utilisateur</div>
                <hit-select
                  class="w-full"
                  :label="'Compte (e-mail)'"
                  :options="userOptions"
                  :value="selectedUsername"
                  item-id-property="id"
                  :label-fields="['label']"
                  clearable
                  :disabled="!userOptions.length"
                  @change="onUserChange"
                />
              </div>

              <div class="generate-form__block">
                <div class="generate-form__block-title">Accès</div>
                <hit-select
                  class="w-full max-w-md"
                  :label="'Niveau d\'accès'"
                  :options="accessLevelOptions"
                  :value="accessLevel"
                  item-id-property="id"
                  :label-fields="['label']"
                  clearable="false"
                  :disabled="!userOptions.length"
                  @change="onAccessLevelChange"
                />
                <p class="generate-form__help">
                  RO : lecture seule — RW : lecture et écriture via l’API.
                </p>
              </div>

              <div class="generate-form__block generate-form__block--billing">
                <div class="generate-form__block-title">Facturation</div>
                <div class="billing-options">
                  <div class="billing-options__toggle">
                    <span class="billing-options__toggle-label"
                      >Facturable</span
                    >
                    <api-key-toggle-switch
                      :on="billable"
                      :disabled="!userOptions.length"
                      @toggle="toggleGenerateBillable"
                    />
                  </div>
                  <div class="price-field">
                    <label class="price-label">Prix / mois (€)</label>
                    <input
                      v-model.number="pricePerMonth"
                      type="number"
                      min="0"
                      step="0.01"
                      class="hit-input"
                      :disabled="!billable || !userOptions.length"
                      placeholder="0.00"
                    />
                    <p v-if="!billable" class="generate-form__help">
                      Activez « Facturable » pour saisir un prix.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="generate-card__actions">
              <hit-button
                :label="'Valider'"
                color="accent"
                prefix-icon="check"
                :disabled="generating || !canGenerate"
                @click="generate"
              />
              <hit-button
                :label="'Réinitialiser'"
                color="button"
                prefix-icon="reset_changes"
                :disabled="generating"
                @click="resetGenerateForm"
              />
            </div>
          </template>
        </div>
      </template>
    </template>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>

    <hit-modal
      v-if="tokenViewVisible"
      title="Clé API"
      prefix-icon="key"
      @cancel="closeTokenView"
    >
      <div v-if="tokenViewRow" class="token-view">
        <p class="token-view__user">{{ formatUserLabel(tokenViewRow) }}</p>
        <p class="token-view__warn">
          Ne partagez pas ce jeton. Toute personne qui le possède peut accéder à
          l’API au nom de cet utilisateur.
        </p>
        <div v-if="tokenLoading" class="token-view__loading">
          Chargement du jeton…
        </div>
        <div v-else-if="tokenLoadError" class="token-view__error">
          {{ tokenLoadError }}
        </div>
        <template v-else-if="tokenValue">
          <pre class="token-view__value">{{ tokenValue }}</pre>
          <div class="token-view__meta">
            <span v-if="tokenJwtId">JTI : {{ tokenJwtId }}</span>
            <span v-if="tokenAccessLevel">Accès : {{ tokenAccessLevel }}</span>
          </div>
        </template>
      </div>
      <div class="flex justify-end gap-2 mt-4">
        <hit-button
          :label="'Fermer'"
          color="button"
          prefix-icon="close"
          @click="closeTokenView"
        />
        <hit-button
          :label="tokenCopied ? 'Copié' : 'Copier'"
          color="accent"
          prefix-icon="copy"
          :disabled="!tokenValue || tokenLoading"
          @click="copyToken"
        />
      </div>
    </hit-modal>

    <hit-modal
      v-if="billingEditVisible"
      title="Modifier"
      prefix-icon="edit"
      @cancel="closeBillingEdit"
    >
      <div v-if="billingEditRow" class="billing-edit-form">
        <p class="billing-edit-user">
          {{ formatUserLabel(billingEditRow) }}
        </p>
        <div class="billing-edit-toggle">
          <span class="billing-options__toggle-label">Facturable</span>
          <api-key-toggle-switch
            :on="billingDraftBillable"
            @toggle="toggleBillingDraftBillable"
          />
        </div>
        <div class="price-field mt-3">
          <label class="block text-sm font-medium text-slate-700 mb-1">
            Prix / mois (€)
          </label>
          <input
            v-model.number="billingDraftPrice"
            type="number"
            min="0"
            step="0.01"
            class="hit-input"
            :disabled="!billingDraftBillable"
            placeholder="0.00"
          />
          <p v-if="!billingDraftBillable" class="generate-form__help">
            Le prix est remis à 0 € lorsque la clé n’est pas facturable.
          </p>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-4">
        <hit-button
          :label="'Annuler'"
          color="button"
          prefix-icon="close"
          @click="closeBillingEdit"
        />
        <hit-button
          :label="'Continuer'"
          color="accent"
          prefix-icon="arrow_forward"
          :disabled="!billingDraftValid"
          @click="proceedBillingConfirm"
        />
      </div>
    </hit-modal>

    <hit-dialog
      :visible="confirmVisible"
      :title="confirmTitle"
      color="accent"
      :ok-button-label="confirmOkLabel"
      ok-button-icon="check"
      ok-button-color="accent"
      cancel-button-icon="close"
      :cancel-button-label="'Annuler'"
      :show-cancel-button="true"
      :ok-button-disabled="patching"
      @ok="executeConfirm"
      @cancel="closeConfirm"
    >
      <template #body>
        <p class="confirm-message">{{ confirmMessage }}</p>
      </template>
    </hit-dialog>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitDialog, HitModal, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';
import ApiKeyToggleSwitch from './ApiKeyToggleSwitch.vue';

export default defineComponent({
  name: 'ScriptManageApiKeys',
  components: {HitButton, HitDialog, HitModal, HitSelect, ApiKeyToggleSwitch},
  data() {
    return {
      companyOptions: [],
      selectedSchemaId: null,
      dataLoading: false,
      apiKeys: [],
      userOptions: [],
      selectedUsername: null,
      accessLevel: 'ro',
      billable: false,
      pricePerMonth: 0,
      generating: false,
      patching: false,
      result: null,
      billingEditVisible: false,
      billingEditRow: null,
      billingDraftBillable: false,
      billingDraftPrice: 0,
      confirmVisible: false,
      confirmType: null,
      confirmRow: null,
      confirmPayload: null,
      tokenViewVisible: false,
      tokenViewRow: null,
      tokenLoading: false,
      tokenValue: null,
      tokenJwtId: null,
      tokenAccessLevel: null,
      tokenLoadError: null,
      tokenCopied: false,
    };
  },
  computed: {
    accessLevelOptions() {
      return [
        {id: 'ro', label: 'Lecture seule (RO)'},
        {id: 'rw', label: 'Lecture / écriture (RW)'},
      ];
    },
    canGenerate() {
      if (!this.selectedSchemaId || !this.selectedUsername) return false;
      if (
        this.billable &&
        (this.pricePerMonth === null || this.pricePerMonth === '')
      ) {
        return false;
      }
      return true;
    },
    billingDraftValid() {
      if (!this.billingDraftBillable) return true;
      return (
        this.billingDraftPrice !== null &&
        this.billingDraftPrice !== '' &&
        !Number.isNaN(Number(this.billingDraftPrice))
      );
    },
    confirmTitle() {
      if (this.confirmType === 'deactivate') return 'Désactiver la clé API';
      if (this.confirmType === 'reactivate') return 'Réactiver la clé API';
      if (this.confirmType === 'billing') return 'Confirmer les modifications';
      if (this.confirmType === 'revoke') return 'Révoquer la clé API';
      return 'Confirmation';
    },
    confirmOkLabel() {
      if (this.confirmType === 'deactivate') return 'Désactiver';
      if (this.confirmType === 'reactivate') return 'Réactiver';
      if (this.confirmType === 'revoke') return 'Révoquer';
      return 'Confirmer';
    },
    confirmMessage() {
      const row = this.confirmRow;
      if (!row) return '';
      const user = this.formatUserLabel(row);
      if (this.confirmType === 'deactivate') {
        return `Voulez-vous désactiver la clé API de ${user} ? Les requêtes avec ce jeton seront refusées.`;
      }
      if (this.confirmType === 'reactivate') {
        return `Voulez-vous réactiver la clé API de ${user} ?`;
      }
      if (this.confirmType === 'billing' && this.confirmPayload) {
        const oldBillable = row.billable ? 'Oui' : 'Non';
        const newBillable = this.confirmPayload.billable ? 'Oui' : 'Non';
        const oldPrice = this.formatPrice(row.price_per_month);
        const newPrice = this.formatPrice(this.confirmPayload.price_per_month);
        return (
          `Confirmer les modifications pour ${user} ?\n\n` +
          `Facturable : ${oldBillable} → ${newBillable}\n` +
          `Prix / mois : ${oldPrice} → ${newPrice}`
        );
      }
      if (this.confirmType === 'revoke') {
        return (
          `Révoquer définitivement la clé API de ${user} ?\n\n` +
          `Le jeton ne fonctionnera plus. L’utilisateur pourra recevoir une nouvelle clé via le formulaire ci-dessous.`
        );
      }
      return '';
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listApiKeyCompanyOptions({limit: 2000});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const parts = [r.designation, r.vat_no, r.schema_id].filter(Boolean);
          const inactive = r.active === false ? ' (inactive)' : '';
          return {
            id: r.schema_id || r.id,
            label: (parts.join(' – ') || r.id) + inactive,
          };
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedSchemaId = value;
      this.result = null;
      this.resetGenerateForm();
      this.closeBillingEdit();
      this.closeConfirm();
      this.closeTokenView();
      this.loadCompanyData();
    },
    resetGenerateForm() {
      this.selectedUsername = null;
      this.accessLevel = 'ro';
      this.billable = false;
      this.pricePerMonth = 0;
    },
    onUserChange(value) {
      this.selectedUsername = value;
      this.result = null;
    },
    onAccessLevelChange(value) {
      this.accessLevel = value || 'ro';
      this.result = null;
    },
    formatUserLabel(row) {
      const name = [row.first_name, row.last_name].filter(Boolean).join(' ');
      if (name) return `${row.username_email} (${name})`;
      return row.username_email || '-';
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      const n = Number(value);
      if (Number.isNaN(n)) return String(value);
      return `${n.toFixed(2)} €`;
    },
    formatDate(value) {
      if (!value) return '-';
      try {
        return new Date(value).toLocaleString();
      } catch {
        return String(value);
      }
    },
    async openTokenView(row) {
      if (!row?.id || !this.selectedSchemaId) return;
      this.tokenViewRow = row;
      this.tokenViewVisible = true;
      this.tokenLoading = true;
      this.tokenValue = null;
      this.tokenJwtId = null;
      this.tokenAccessLevel = null;
      this.tokenLoadError = null;
      this.tokenCopied = false;
      try {
        const res = await adminApi.getCompanyApiKeyToken(
          row.id,
          this.selectedSchemaId
        );
        if (this.tokenViewRow?.id !== row.id) return;
        this.tokenValue = res?.jwt_token || null;
        this.tokenJwtId = res?.jwt_id || null;
        this.tokenAccessLevel = res?.access_level || null;
        if (!this.tokenValue) {
          this.tokenLoadError = 'Jeton introuvable.';
        }
      } catch (e) {
        if (this.tokenViewRow?.id === row.id) {
          this.tokenLoadError = e?.message || String(e);
        }
      } finally {
        if (this.tokenViewRow?.id === row.id) {
          this.tokenLoading = false;
        }
      }
    },
    closeTokenView() {
      this.tokenViewVisible = false;
      this.tokenViewRow = null;
      this.tokenValue = null;
      this.tokenJwtId = null;
      this.tokenAccessLevel = null;
      this.tokenLoadError = null;
      this.tokenCopied = false;
    },
    async copyToken() {
      if (!this.tokenValue) return;
      try {
        await navigator.clipboard.writeText(this.tokenValue);
        this.tokenCopied = true;
        setTimeout(() => {
          this.tokenCopied = false;
        }, 2000);
      } catch {
        this.result = {
          ok: false,
          message: 'Impossible de copier le jeton dans le presse-papiers.',
        };
      }
    },
    toggleGenerateBillable() {
      if (!this.userOptions.length) return;
      this.billable = !this.billable;
      if (!this.billable) {
        this.pricePerMonth = 0;
      }
    },
    toggleBillingDraftBillable() {
      this.billingDraftBillable = !this.billingDraftBillable;
      if (!this.billingDraftBillable) {
        this.billingDraftPrice = 0;
      }
    },
    onBillableSwitchClick(row) {
      if (this.patching) return;
      if (!row.billable) {
        this.openBillingEdit(row);
        this.billingDraftBillable = true;
        return;
      }
      this.confirmRow = row;
      this.confirmType = 'billing';
      this.confirmPayload = {billable: false, price_per_month: 0};
      this.confirmVisible = true;
    },
    onActiveSwitchClick(row) {
      if (this.patching) return;
      this.openActiveConfirm(row, !row.active);
    },
    openRevokeConfirm(row) {
      this.confirmRow = row;
      this.confirmType = 'revoke';
      this.confirmPayload = null;
      this.confirmVisible = true;
    },
    openActiveConfirm(row, activate) {
      this.confirmRow = row;
      this.confirmType = activate ? 'reactivate' : 'deactivate';
      this.confirmPayload = {active: activate};
      this.confirmVisible = true;
    },
    openBillingEdit(row) {
      this.billingEditRow = row;
      this.billingDraftBillable = !!row.billable;
      this.billingDraftPrice = Number(row.price_per_month) || 0;
      this.billingEditVisible = true;
    },
    closeBillingEdit() {
      this.billingEditVisible = false;
      this.billingEditRow = null;
    },
    proceedBillingConfirm() {
      const row = this.billingEditRow;
      if (!row || !this.billingDraftValid) return;
      const billable = !!this.billingDraftBillable;
      const price = billable ? Number(this.billingDraftPrice) : 0;
      const unchanged =
        !!row.billable === billable &&
        Number(row.price_per_month || 0) === price;
      if (unchanged) {
        this.closeBillingEdit();
        return;
      }
      this.closeBillingEdit();
      this.confirmRow = row;
      this.confirmType = 'billing';
      this.confirmPayload = {billable, price_per_month: price};
      this.confirmVisible = true;
    },
    closeConfirm() {
      this.confirmVisible = false;
      this.confirmType = null;
      this.confirmRow = null;
      this.confirmPayload = null;
    },
    async executeConfirm() {
      const row = this.confirmRow;
      const payload = this.confirmPayload;
      if (!row?.id) return;
      if (this.confirmType !== 'revoke' && !payload) return;

      this.patching = true;
      this.result = null;
      try {
        let res;
        if (this.confirmType === 'revoke') {
          res = await adminApi.revokeCompanyApiKey(
            row.id,
            this.selectedSchemaId
          );
        } else {
          res = await adminApi.patchCompanyApiKey(row.id, payload);
        }
        this.result = {
          ok: true,
          message: res?.message || 'Mise à jour effectuée.',
        };
        this.closeConfirm();
        await this.loadCompanyData();
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.patching = false;
      }
    },
    async loadCompanyData() {
      const schemaId = this.selectedSchemaId;
      if (!schemaId) {
        this.apiKeys = [];
        this.userOptions = [];
        return;
      }

      this.dataLoading = true;
      try {
        const [keysRes, usersRes] = await Promise.all([
          adminApi.listCompanyApiKeys(schemaId),
          adminApi.listCompanyUsersWithoutApiKey(schemaId),
        ]);
        if (this.selectedSchemaId !== schemaId) return;
        this.apiKeys = keysRes?.rows || [];
        this.userOptions = (usersRes?.rows || []).map((u) => {
          const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
          const label = name
            ? `${u.username_email} (${name})`
            : u.username_email;
          return {id: u.username_email, label};
        });
      } catch (e) {
        if (this.selectedSchemaId === schemaId) {
          this.apiKeys = [];
          this.userOptions = [];
          this.result = {ok: false, message: e?.message || String(e)};
        }
      } finally {
        if (this.selectedSchemaId === schemaId) {
          this.dataLoading = false;
        }
      }
    },
    async generate() {
      if (!this.canGenerate) return;
      this.generating = true;
      this.result = null;
      try {
        const payload = {
          schema_id: this.selectedSchemaId,
          username: this.selectedUsername,
          access_level: this.accessLevel,
          billable: this.billable,
          price_per_month: this.billable ? Number(this.pricePerMonth) : 0,
        };
        const res = await adminApi.generateCompanyApiKey(payload);
        this.result = {ok: true, message: res?.message || 'Clé générée.'};
        this.resetGenerateForm();
        await this.loadCompanyData();
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.generating = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
  padding: 1.25rem;
}

.panel-title {
  font-weight: 700;
  font-size: 1.05rem;
}

.panel-subtitle {
  margin-top: 0.35rem;
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
}

.panel-grid {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
}

.section-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: rgba(15, 23, 42, 0.9);
}

.keys-table-wrap {
  margin-top: 0.5rem;
  overflow-x: auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
}

.keys-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    vertical-align: middle;
  }

  th {
    background: rgba(248, 250, 252, 1);
    font-weight: 600;
    color: rgba(51, 65, 85, 1);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.actions-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.access-pill {
  display: inline-block;
  padding: 0.1rem 0.45rem;
  border-radius: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.access-ro {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}

.access-rw {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.price-cell__btn {
  padding: 0;
  border: none;
  background: none;
  font-size: inherit;
  color: #1d4ed8;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.price-cell__btn:hover:not(:disabled) {
  color: #1e3a8a;
}

.price-cell__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.price-cell__na {
  color: rgba(148, 163, 184, 1);
}

.billing-options__toggle,
.billing-edit-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.billing-options__toggle-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(51, 65, 85, 1);
}

.generate-card {
  margin-top: 0.65rem;
  max-width: 42rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.75rem;
  background: rgba(248, 250, 252, 0.65);
  padding: 1rem 1.15rem 1.1rem;
}

.generate-card__hint {
  margin: 0 0 1rem;
  font-size: 0.875rem;
  line-height: 1.4;
  color: rgba(100, 116, 139, 1);
}

.generate-card__alert {
  margin-bottom: 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(254, 243, 199, 0.85);
  border: 1px solid rgba(245, 158, 11, 0.25);
  font-size: 0.875rem;
  color: rgba(120, 53, 15, 1);
}

.generate-form {
  display: grid;
  gap: 1.15rem;
}

.generate-form__block {
  display: grid;
  gap: 0.5rem;
}

.generate-form__block-title {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(100, 116, 139, 1);
}

.generate-form__help {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.35;
  color: rgba(148, 163, 184, 1);
}

.generate-form__block--billing {
  padding-top: 0.15rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.billing-options {
  display: grid;
  gap: 0.85rem;
}

.price-field {
  display: grid;
  gap: 0.35rem;
}

.price-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(51, 65, 85, 1);
}

.generate-card__actions {
  margin-top: 1.15rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.hit-input {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 0.5rem;
  padding: 0.45rem 0.6rem;
  font-size: 0.875rem;
}

.billing-edit-user {
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: rgba(51, 65, 85, 1);
}

.token-view__user {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(51, 65, 85, 1);
}

.token-view__warn {
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  line-height: 1.35;
  color: rgba(180, 83, 9, 1);
}

.token-view__loading,
.token-view__error {
  font-size: 0.875rem;
  color: rgba(100, 116, 139, 1);
}

.token-view__error {
  color: #b91c1c;
}

.token-view__value {
  margin: 0;
  max-height: 10rem;
  overflow: auto;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(248, 250, 252, 1);
  font-size: 0.75rem;
  line-height: 1.35;
  word-break: break-all;
  white-space: pre-wrap;
}

.token-view__meta {
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: rgba(100, 116, 139, 1);
}

.confirm-message {
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.4;
  color: rgba(51, 65, 85, 1);
}

.empty-keys {
  margin-top: 0.5rem;
  color: rgba(100, 116, 139, 1);
  font-size: 0.875rem;
}

.status-pill {
  color: rgba(100, 116, 139, 1);
  font-size: 0.875rem;
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
